import { FormLabel } from '@mui/material';
import { useFormikContext } from 'formik';
import { FC, useMemo, useState } from 'react';
import { FileWithPath } from 'react-dropzone/.';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { Box } from 'src/components/Box';
import { Button } from 'src/components/Button';
import { HtmlTypography } from 'src/components/HtmlTypography';
import { LinearProgress } from 'src/components/LinearProgress';
import { Link } from 'src/components/Link';
import { Meta } from 'src/components/Meta';
import { RadioGroup } from 'src/components/RadioGroup';
import { Stack } from 'src/components/Stack';
import { StickyCard } from 'src/components/StickyCard';
import { Typography } from 'src/components/Typography';
import { InlineFileUploader } from 'src/components/Uploader/InlineFileUploader';
import { useSession } from 'src/contexts/SessionContext';
import { externalLinks } from 'src/externalLinks';
import { Exercise } from 'src/models/exercise/_types';
import {
  createExerciseThumbnail,
  fetchExercise,
} from 'src/models/exercise/exercise';
import {
  fetchGroupVideo,
  fetchVideo,
  handleGroupVideoFileUpload,
} from 'src/models/video/video';
import { AppFrameConfig } from 'src/shared/AppFrame/AppFrameConfig';
import { FormCheckbox } from 'src/shared/FormCheckbox';
import { FormTextField } from 'src/shared/Forms/Fields/FormTextField';
import { ExerciseAutocomplete } from 'src/shared/Forms/Selects/ExerciseAutocomplete';
import { FormTagsSelect } from 'src/shared/Forms/Selects/TagsSelect';
import { Scheduling } from 'src/shared/Scheduling';
import { Identifier } from 'src/types/utilityTypes';
import {
  handleFileSelect,
  handleVideoUploadWithProcessing,
} from 'src/utils/fileHandlers';
import toast from 'src/utils/toast';

interface NewVideoLayoutProps {
  videoId?: string;
  // groupId is only used for group videos page.
  groupId?: string;
  loading?: boolean;
}

const NewVideoLayout: FC<NewVideoLayoutProps> = ({
  videoId,
  groupId,
  loading,
}) => {
  const { t } = useTranslation('translation', { keyPrefix: 'group.video' });
  const { t: tButtons } = useTranslation('translation', {
    keyPrefix: 'common.buttons',
  });
  const { t: tGlobal } = useTranslation();
  const { submitForm, setFieldValue, values } = useFormikContext<any>();
  const { session, user } = useSession();
  const navigate = useNavigate();

  const isGroup = !!groupId;

  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadedVideoId, setUploadedVideoId] = useState<
    Identifier | undefined
  >(undefined);

  const [thumbnailUploading, setThumbnailUploading] = useState(false);
  const [thumbnailUploadProgress, setThumbnailUploadProgress] = useState(0);

  const [inputType, setInputType] = useState<'file' | 'url' | 'existing'>(
    'url'
  );

  const [selectedExercise, setSelectedExercise] = useState<Exercise | null>(
    null
  );

  const { data: uploadedVideo, isLoading: loadingVideo } = useQuery({
    queryKey: ['video', uploadedVideoId],
    queryFn: () => fetchVideo({ id: uploadedVideoId as Identifier }),
    enabled: !!uploadedVideoId,
  });

  const { data: groupVideo } = useQuery({
    queryKey: ['groupVideo', videoId],
    queryFn: async () => {
      if (!groupId) {
        return await fetchVideo({ id: videoId as Identifier });
      }
      return await fetchGroupVideo({ groupVideoId: videoId as Identifier });
    },
    enabled: !!videoId && videoId !== 'new',
  });

  const createThumbnailMutation = useMutation({
    mutationFn: (file: FileWithPath) => {
      if (isGroup) {
        return handleGroupVideoFileUpload(file);
      }
      return createExerciseThumbnail(file, user);
    },
    onError: () => {
      toast.error(t('Failed to upload thumbnail'));
    },
  });

  const handleExerciseChange = async (
    exerciseId: Identifier | undefined,
    setFieldValue: (field: string, value: any) => void,
    values: any
  ) => {
    if (!exerciseId) return;
    const exercise = await fetchExercise({ id: exerciseId });
    if (
      exercise.videoUrl &&
      (exercise.videoUrl.includes('vimeo') ||
        exercise.videoUrl.includes('youtu') ||
        exercise.videoUrl.includes('/'))
    ) {
      setFieldValue('url', exercise.videoUrl);
    } else {
      setFieldValue('url', `https://youtube.com/watch?v=${exercise.videoUrl}`);
    }
    if (!values.thumbnailUrl) {
      setFieldValue('thumbnailUrl', exercise.thumbnail);
    }
    setInputType('url');
  };

  const config = useMemo(() => {
    if (!isGroup) {
      return {
        backButtonLinkTo: '/videos',
        title: videoId === 'new' ? t('New Video') : t('Editing Video'),
      };
    }
    return {
      backButtonLinkTo: `/groups/${groupId}/videos`,
      title:
        videoId === 'new' ? t('New Group Video') : t('Editing Group Video'),
    };
  }, [groupId, isGroup, t, videoId]);

  const radioOptions = useMemo(() => {
    const options = [
      { label: t('Upload File'), value: 'file' },
      { label: t('Enter URL'), value: 'url' },
    ];
    if (!groupId) {
      return options;
    }
    return [...options, { label: t('Use Existing Video'), value: 'existing' }];
  }, [t, groupId]);

  return (
    <Box mb={3}>
      <AppFrameConfig
        id="groupVideo"
        containerMaxWidth={'xl'}
        backButtonTitle={'videos'}
        backButtonLinkTo={config.backButtonLinkTo}
        title={config.title}
      />
      <StickyCard
        title={config.title}
        actions={
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="contained"
              color="primary"
              loading={loading}
              onClick={() => {
                submitForm();
              }}
            >
              {tButtons('save')}
            </Button>
            <Button
              type="button"
              variant="outlined"
              color="primary"
              onClick={() => {
                navigate(config.backButtonLinkTo, { replace: true });
              }}
            >
              {tButtons('cancel')}
            </Button>
          </Box>
        }
      >
        <Meta title={config.title} brand={session?.brand} />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
          <FormTextField name="name" label={t('Name')} fullWidth required />
          <RadioGroup
            options={radioOptions}
            label=""
            value={inputType}
            onChange={(e) => {
              setInputType(e.target.value as 'file' | 'url' | 'existing');
              setFieldValue('inputType', e.target.value);
            }}
          />

          {inputType === 'file' ? (
            <>
              <InlineFileUploader
                accept={{ 'video/*': [] }}
                maxFileSize={10000 * 1024 * 1024}
                maxFileSizeCaption={t('Max file size is 10,000 MB')}
                onFileSelect={async (files) => {
                  if (!files) {
                    setFieldValue('url', null);
                    setFieldValue('videoId', null);
                    setFieldValue('embedUrl', null);
                    setUploadedVideoId(undefined);
                    setFieldValue('uploadedVideo', null);
                    return;
                  }
                  if (isGroup) {
                    handleFileSelect({
                      selectedFiles: files ? [files] : [],
                      setUploadingState: setUploading,
                      setProgressState: setUploadProgress,
                      setAssetState: () => void 0,
                      setUploadedVideoId: (id) => {
                        setFieldValue('url', null);
                        setFieldValue('videoId', id);
                        setFieldValue('embedUrl', null);
                        setUploadedVideoId(id);
                      },
                    });
                  } else {
                    setUploading(true);
                    setUploadProgress(0);
                    await handleVideoUploadWithProcessing(
                      files,
                      {
                        onUploadProgress: setUploadProgress,
                        onUploadComplete: async (videoId) => {
                          setFieldValue('videoId', videoId);
                          setFieldValue('uploadedVideo', videoId);
                        },
                        onProcessingComplete: () => {
                          setUploading(false);
                        },
                        onError: () => {
                          setUploading(false);
                          setUploadProgress(0);
                          toast.error(
                            tGlobal('client.progressPhotos.failedToUploadVideo')
                          );
                        },
                      },
                      {
                        videoId: videoId,
                      }
                    );
                  }
                }}
                multiple={false}
              />
              {uploading && (
                <Box sx={{ width: '100%' }}>
                  <LinearProgress
                    variant="determinate"
                    value={uploadProgress}
                  />
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    align="center"
                  >
                    {uploadProgress === 100
                      ? t('Upload complete')
                      : `${Math.round(uploadProgress)}% ${t(
                          'Uploaded'
                        ).toLowerCase()}`}
                  </Typography>
                </Box>
              )}
              {loadingVideo && (
                <Typography>{t('Loading video data')}</Typography>
              )}
              {isGroup && uploadedVideo && uploadedVideo.name && (
                <Typography variant="body2">
                  {t('Uploaded')} {uploadedVideo.name}
                </Typography>
              )}
            </>
          ) : inputType === 'url' ? (
            <FormTextField
              name="url"
              label={t('YouTube/Vimeo URL')}
              fullWidth
            />
          ) : inputType === 'existing' ? (
            <ExerciseAutocomplete
              label={t('Select Exercise Video')}
              value={selectedExercise}
              onChange={(exercise) => {
                setSelectedExercise(exercise);
                handleExerciseChange(exercise?.id, setFieldValue, values);
              }}
            />
          ) : null}
          {values.url && (
            <Typography variant="body2">
              {t('Current Video')}:{' '}
              <Box
                component={Link}
                href={values.url}
                target="_blank"
                rel="noopener noreferrer"
              >
                {values.name || values.url}
              </Box>
            </Typography>
          )}
          <FormTextField
            name="description"
            label={t('Description (Optional)')}
            fullWidth
          />
          <FormTextField
            name="fullDescription"
            label={t('Long Description (Optional)')}
            fullWidth
            multiline
            rows={4}
            helperText={t(
              'The long description shows when viewing an individual video.'
            )}
          />
          <FormTextField
            name="thumbnailUrl"
            label={t('Thumbnail URL (Optional)')}
            fullWidth
          />
          <Box display="flex" flexDirection="column" gap={1}>
            <FormLabel>{t('Thumbnail Image Upload (Optional)')}</FormLabel>
            {values.thumbnailPreview && !isGroup ? (
              <Stack direction="row" alignItems="center" gap={2}>
                <img
                  src={values.thumbnailPreview}
                  alt={values.thumbnailPreview}
                  width={200}
                />
                <Link
                  component="button"
                  onClick={() => {
                    setFieldValue('thumbnailUrl', '');
                    setFieldValue('thumbnailFile', null);
                    setFieldValue('thumbnailPreview', null);
                    setFieldValue('removeThumbnailFile', true);
                  }}
                >
                  {t('remove')}
                </Link>
              </Stack>
            ) : (
              <InlineFileUploader
                accept={{ 'image/*': [] }}
                maxFileSize={(isGroup ? 32 : 6) * 1024 * 1024}
                maxFileSizeCaption={
                  <HtmlTypography>
                    {isGroup
                      ? t('max file size caption for group')
                      : t('max file size caption')}
                  </HtmlTypography>
                }
                onFileSelect={async (files) => {
                  if (!files) return;
                  handleFileSelect({
                    selectedFiles: files ? [files] : [],
                    setUploadingState: setThumbnailUploading,
                    setProgressState: setThumbnailUploadProgress,
                    handleUpload: (file) =>
                      createThumbnailMutation.mutateAsync(file),
                    setAssetState: (result) => {
                      setFieldValue('thumbnailFile', result.id);
                      const reader = new FileReader();
                      reader.onload = (e) => {
                        setFieldValue('thumbnailPreview', e.target?.result);
                      };
                      reader.readAsDataURL(files);
                    },
                  });
                }}
                multiple={false}
              />
            )}
            {thumbnailUploading && (
              <Box sx={{ width: '100%' }}>
                <LinearProgress
                  variant="determinate"
                  value={thumbnailUploadProgress}
                />
                <Typography
                  variant="body2"
                  color="text.secondary"
                  align="center"
                >
                  {thumbnailUploadProgress === 100
                    ? t('Upload complete')
                    : `${Math.round(thumbnailUploadProgress)}% uploaded`}
                </Typography>
              </Box>
            )}
          </Box>
          <FormTagsSelect
            name="tags"
            label={t('Tags')}
            tagType="video"
            multiple
          />
          {isGroup && (
            <Scheduling
              showSendNotification={!groupVideo?.id}
              type={t('one').toLowerCase()}
            />
          )}
          {!isGroup && (
            <>
              <FormCheckbox
                name="autoAssign"
                label={t('Auto Assign Label')}
                onChange={(e) => {
                  setFieldValue('autoAssign', e.target.checked);
                  setFieldValue('assignExisting', false);
                }}
              />
              {values.autoAssign && (
                <FormCheckbox
                  name="assignExisting"
                  label={t('Assign Existing')}
                />
              )}
              <FormCheckbox name="public" label={t('Public Label')} />
              {values.public && (
                <Typography variant="body2">
                  {t('You can view this video')}{' '}
                  <Link
                    href={externalLinks.viewVideo(
                      values.videoId || (videoId === 'new' ? '' : videoId)
                    )}
                    target="_blank"
                  >
                    {t('here')}
                  </Link>
                </Typography>
              )}
            </>
          )}
        </Box>
      </StickyCard>
    </Box>
  );
};

export default NewVideoLayout;
